import type { ConfettiConfig, ConfettiShape } from './types';

export const DEFAULT_CONFETTI_COLORS: string[] = [
  '#FFD700', // Gold
  '#FF6B6B', // Coral Red
  '#4ECDC4', // Teal
  '#A78BFA', // Purple
  '#F472B6', // Pink
  '#34D399', // Emerald
  '#60A5FA', // Sky Blue
  '#FBBF24', // Amber
];

/** Grayscale sparkle palette with high contrast */
export const BLENDED_CONFETTI_COLORS: string[] = [
  '#303030',
  '#404040',
  '#505050',
  '#606060',
  '#707070',
  '#808080',
  '#909090',
  '#A0A0A0',
  '#B0B0B0',
  '#C0C0C0',
  '#D0D0D0',
  '#E0E0E0',
  '#F0F0F0',
  '#FFFFFF',
];

export const DEFAULT_CONFETTI_CONFIG: Required<ConfettiConfig> = {
  particleCount: 50,
  angle: 90,
  spread: 45,
  startVelocity: 45,
  decay: 0.9,
  gravity: 1,
  drift: 0,
  ticks: 200,
  x: 0.5,
  y: 0.5,
  shapes: ['square', 'circle'],
  colors: DEFAULT_CONFETTI_COLORS,
  scalar: 1,
  width: 1280,
  height: 720,
} as const;

/** Physics-related constants for particle simulation */
export const PHYSICS = {
  DEG_TO_RAD: Math.PI / 180,
  /** Multiplier to convert video frames to confetti ticks (60fps / 30fps = 2) */
  TICK_MULTIPLIER: 2,
  /** Gravity constant matching canvas-confetti */
  GRAVITY_CONSTANT: 3,
  OVAL_SCALAR: 0.6,
  RANDOM_OFFSET_BASE: 2,
  ELLIPSE_ROTATION_FACTOR: Math.PI / 10,
} as const;

/** Wobble animation constants */
export const WOBBLE = {
  AMPLITUDE: 10,
  SPEED_MAX: 0.11,
  SPEED_RANGE: 0.1,
  SPEED_MIN: 0.05,
} as const;

/** Tilt animation constants */
export const TILT = {
  SPEED: 0.1,
  ANGLE_MIN: 0.25,
  ANGLE_RANGE: 0.5,
} as const;

/** Fallback values for safe array access */
export const FALLBACK = {
  SHAPE: 'square' as ConfettiShape,
  COLOR: { r: 255, g: 255, b: 255 },
} as const;

/** Confetti animation configuration for Scene2 */
export const CONFETTI_CONFIG_SCENE2 = {
  PARTICLES_PER_BURST: 200, // particles per burst
  ANGLE: 90, // shoot upward
  SPREAD: 90, // cone width
  TICKS: 430, // particle lifetime
  GRAVITY: 2, // fall speed
  SCALAR: 1.75, // particle size scale
  DRIFT: 0.25, // horizontal drift
  Z_INDEX: 100, // layer order
  START_VELOCITY: 100, // launch speed
  DECAY: 0.9, // speed loss over time
  SHAPES: ['square'] as ConfettiShape[], // shapes to use
  BURST_LOCATIONS: [
    { x: 0.5, ratio: 1.0 }, // Center
    { x: 0.25, ratio: 0.6 }, // Left
    { x: 0.75, ratio: 0.6 }, // Right
    { x: 0.1, ratio: 0.4 }, // Far Left
    { x: 0.9, ratio: 0.4 }, // Far Right
  ],
} as const;
